/**
 * Greek-market field validation.
 *
 * The spec models `tax_number` as a free VARCHAR(50). In practice a Greek brokerage
 * keys AFM constantly and a transposed digit silently corrupts the book of business,
 * so we validate the official checksums here and share the rules with both tiers.
 */

/**
 * Greek Tax Identification Number (ΑΦΜ). 9 digits, weighted mod-11 checksum:
 * each of the first 8 digits is weighted by 2^(9-position), summed, mod 11, mod 10.
 */
export function isValidAFM(value: string): boolean {
  const afm = value.replace(/\s/g, '');
  if (!/^\d{9}$/.test(afm)) return false;
  // "000000000" passes the arithmetic but is never a real AFM.
  if (/^0+$/.test(afm)) return false;

  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += Number(afm[i]) * 2 ** (8 - i);
  }
  return (sum % 11) % 10 === Number(afm[8]);
}

/**
 * Greek social security number (ΑΜΚΑ). 11 digits; first 6 encode DDMMYY and the
 * last digit is a Luhn check.
 */
export function isValidAMKA(value: string): boolean {
  const amka = value.replace(/\s/g, '');
  if (!/^\d{11}$/.test(amka)) return false;
  if (/^0+$/.test(amka)) return false;

  const day = Number(amka.slice(0, 2));
  const month = Number(amka.slice(2, 4));
  if (day < 1 || day > 31 || month < 1 || month > 12) return false;

  return luhn(amka);
}

function luhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * Greek vehicle registration plate. Post-1983 format is 3 letters + 4 digits, using
 * only the 14 letters shared between the Greek and Latin alphabets. Operators type
 * these in either alphabet, so both are accepted and normalised to Latin.
 */
const PLATE_LATIN = 'ABEZHIKMNOPTYX';
const PLATE_GREEK = 'ΑΒΕΖΗΙΚΜΝΟΡΤΥΧ';

export function normalizePlate(value: string): string {
  const cleaned = value.toUpperCase().replace(/[\s-]/g, '');
  return [...cleaned]
    .map((ch) => {
      const idx = PLATE_GREEK.indexOf(ch);
      return idx >= 0 ? PLATE_LATIN[idx]! : ch;
    })
    .join('');
}

export function isValidPlate(value: string): boolean {
  const plate = normalizePlate(value);
  return new RegExp(`^[${PLATE_LATIN}]{3}\\d{3,4}$`).test(plate);
}

/** Formats a plate for display as ΑΒΓ-1234 (Latin letters, hyphenated). */
export function formatPlate(value: string): string {
  const plate = normalizePlate(value);
  return isValidPlate(plate) ? `${plate.slice(0, 3)}-${plate.slice(3)}` : value;
}

/**
 * How a policy is named wherever it is shown: the plate first, the policy number in
 * brackets behind it.
 *
 * The order is how the office actually talks. Nobody says «818861529 λήγει σε 15 μέρες»;
 * they say «το ΥΗΚ3984 λήγει», and then look the number up to type it. A notification
 * headed by the number alone has to be opened before it can be recognised, which is the
 * one thing a notification is supposed to save.
 *
 * Health, property and life policies insure no vehicle, so `plates` is empty and the
 * number stands alone — never an empty bracket. A policy carrying more than one car shows
 * the first plate and a count, because the line has to stay one line.
 *
 * `normalizePlate`, not `formatPlate`: the office writes ΥΗΚ3984 in one piece, and this
 * has to match what the plate filter and the validator see, or the same car reads two ways
 * on two screens.
 */
export function policyRef(
  policyNumber: string,
  plates?: readonly string[] | null,
): { plate: string | null; more: number; number: string } {
  const list = (plates ?? []).filter((plate) => plate && plate.trim().length > 0);
  return {
    plate: list.length > 0 ? normalizePlate(list[0]!) : null,
    more: Math.max(0, list.length - 1),
    number: policyNumber,
  };
}

/** The same label as one string, for toasts, page titles, exports and e-mail subjects. */
export function formatPolicyRef(
  policyNumber: string,
  plates?: readonly string[] | null,
): string {
  const { plate, more, number } = policyRef(policyNumber, plates);
  if (!plate) return number;
  return `${plate}${more > 0 ? ` +${more}` : ''} (${number})`;
}

/** IBAN mod-97 check (ISO 13616). Used for direct-debit and payout details. */
export function isValidIBAN(value: string): boolean {
  const iban = value.toUpperCase().replace(/[\s-]/g, '');
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = [...rearranged]
    .map((ch) => (/[A-Z]/.test(ch) ? (ch.charCodeAt(0) - 55).toString() : ch))
    .join('');

  // Chunked mod-97 to stay inside safe integer range.
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/** Greek mobile and landline numbers, with or without +30. */
export function isValidGreekPhone(value: string): boolean {
  const phone = value.replace(/[\s()-]/g, '');
  return /^(\+30)?[2-7]\d{9}$/.test(phone);
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
}
